import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { DealEntity } from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { dealQueryKey } from './useDealQuery'
import { DEALS_QUERY_KEY_ROOT } from './useDealsQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

// The prototype only ships a one-way Cancel flow; this hook backs the
// "Reactivate Subscription" button that surfaces in EditDealModal once a
// deal is canceled, mirroring the backend uncancel endpoint.
export function useUncancelDealMutation(): UseMutationResult<DealEntity, unknown, string> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<DealEntity, unknown, string>(
    (dealId) => dealsAPI.uncancelDeal(dealId),
    {
      onSuccess: (deal) => {
        toast.current?.show({
          severity: 'success',
          summary: `Subscription reactivated — ${deal.orderNumber}`,
          detail: 'Deal is back in the active list.',
          life: 3000,
        })
        queryClient.invalidateQueries({ queryKey: DEALS_QUERY_KEY_ROOT })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(deal.id) })
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(deal.id) })
      },
      onError: handleError,
    },
  )
}
